import {
  doc,
  setDoc,
  getDoc,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  arrayUnion,
  deleteDoc,
  orderBy,
  limit,
  onSnapshot,
  increment,
  startAfter,
} from 'firebase/firestore';
import { db, storage } from './firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { compressImage, detectAspectRatio } from './imageCompress';


// =====================================================================
// CONNECT ID GENERATOR
// Format: XXX-XXXX (3 letters + 4 alphanumerics, e.g. "MUM-7K3P")
// Used for invite links: https://aangan.aapsaathi.com/join/{connectId}
// =====================================================================

const CONNECT_ID_LETTERS = 'ABCDEFGHJKLMNPQRSTUVWXYZ'; // no I or O — easy to misread
const CONNECT_ID_DIGITS = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ'; // no 0/1/I/O

function generateConnectId() {
  const pick = (chars, n) => Array.from({ length: n }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  return `${pick(CONNECT_ID_LETTERS, 3)}-${pick(CONNECT_ID_DIGITS, 4)}`;
}

/**
 * Normalize a string for fuzzy/case-insensitive search.
 * - Lowercase
 * - Strip punctuation (St. → St, R.K. → RK)
 * - Collapse multiple spaces
 * - Trim
 *
 * Examples:
 *   "St. Mary's High School"  → "st marys high school"
 *   "D.P.S. R.K. Puram"       → "dps rk puram"
 *   "Infosys  Bengaluru"      → "infosys bengaluru"
 */
export function normalizeSearchKey(s) {
  if (!s || typeof s !== 'string') return '';
  return s
    .toLowerCase()
    .replace(/[.''`,!?@#$%^&*()_\-+=\[\]{}|\\:;"<>/]/g, '') // strip punctuation
    .replace(/\s+/g, ' ')                                    // collapse whitespace
    .trim();
}

/**
 * Look up a group by its connectId.
 * Returns the group object with id, or null if not found.
 *
 * NOTE: This requires a Firestore index on the `connectId` field of /groups.
 * If the query fails with "needs an index", create one in Firebase Console.
 */
export async function findGroupByConnectId(connectId) {
  if (!connectId) return null;
  const q = query(collection(db, 'groups'), where('connectId', '==', connectId));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}


// =====================================================================
// GROUP HELPERS — Firestore writes/reads for batches
// =====================================================================

/**
 * Create a new group in Firestore.
 * Writes to:
 *   /groups/{groupId}                       — the group doc
 *   /groups/{groupId}/members/{userId}      — creator as admin member
 *   /users/{userId}.memberOfGroupIds        — append new group ID
 */
export async function createGroup({ creatorId, type, name, passedOut, city, place, project, tower, flat, locality, bio, emoji }) {
  if (!creatorId) throw new Error('Must be signed in to create a group');
  if (!type || !name) throw new Error('Group type and name are required');

  const groupData = {
    type,
    name,
    searchKey: normalizeSearchKey(name),  // for case-insensitive prefix search
    bio: bio || '',
    emoji: emoji || '🏷️',
    photoUrl: null,
    createdBy: creatorId,
    createdAt: serverTimestamp(),
    memberCount: 1,
    adminIds: [creatorId],
    connectId: generateConnectId(),  // unique permanent invite link code
  };
  if (passedOut) groupData.passedOut = passedOut;
  if (city) groupData.city = city;
  if (place) groupData.place = place;
  if (project) groupData.project = project;
  if (tower) groupData.tower = tower;
  if (flat) groupData.flat = flat;
  if (locality) groupData.locality = locality;

  const groupRef = await addDoc(collection(db, 'groups'), groupData);

  await setDoc(doc(db, 'groups', groupRef.id, 'members', creatorId), {
    joinedAt: serverTimestamp(),
    approvedAt: serverTimestamp(),
    viaCode: false,
    approvalDeadline: null,
    isAdmin: true,
    status: 'active',
  });

  await setDoc(doc(db, 'users', creatorId), {
    memberOfGroupIds: arrayUnion(groupRef.id),
  }, { merge: true });

  return {
    id: groupRef.id,
    ...groupData,
  };
}

/**
 * Load all groups the given user is a member of.
 * Reads /users/{userId}.memberOfGroupIds and fetches each group doc.
 */
export async function loadUserGroups(userId) {
  if (!userId) return [];

  const userSnap = await getDoc(doc(db, 'users', userId));
  if (!userSnap.exists()) return [];

  const ids = userSnap.data().memberOfGroupIds || [];
  if (ids.length === 0) return [];

  const groupSnaps = await Promise.all(
    ids.map(id => getDoc(doc(db, 'groups', id)))
  );
  return groupSnaps
    .filter(snap => snap.exists())
    .map(snap => ({ id: snap.id, ...snap.data() }));
}
// =====================================================================
// EVENT HELPERS — birthdays, anniversaries, group events
// Each event lives at /groups/{groupId}/events/{eventId}
// Visibility: scoped to the group it was created in
// =====================================================================

/**
 * Add an event to a group's calendar.
 * Anyone in the batch can add an event.
 *
 * Required: groupId, creatorId, type, title, date (YYYY-MM-DD)
 * Optional: description, recurring ('yearly' | 'once'), monthDay (auto-derived)
 *
 * Returns the new event with its Firestore-generated ID.
 */
export async function addGroupEvent({ groupId, creatorId, type, title, date, description, recurring }) {
  if (!groupId) throw new Error('Group ID is required');
  if (!creatorId) throw new Error('Must be signed in to add an event');
  if (!type || !title || !date) throw new Error('Type, title, and date are required');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) throw new Error('Date must be in YYYY-MM-DD format');

  // Default recurring: birthdays/anniversaries repeat yearly, events are once
  const isAnniversaryType = (type === 'birthday' || type === 'anniversary');
  const finalRecurring = recurring || (isAnniversaryType ? 'yearly' : 'once');

  // monthDay is what we match against today for auto-posting (e.g. "08-14" for Aug 14)
  const monthDay = date.slice(5);

  const eventData = {
    type,                         // 'birthday' | 'anniversary' | 'event'
    title,                        // e.g. "Priya's son" or "Diwali Party"
    date,                         // full date "YYYY-MM-DD" — original year matters for "they turn N"
    monthDay,                     // "MM-DD" — used for yearly recurring matching
    description: description || '',
    recurring: finalRecurring,    // 'yearly' | 'once'
    createdBy: creatorId,
    createdAt: serverTimestamp(),
  };

  const eventRef = await addDoc(collection(db, 'groups', groupId, 'events'), eventData);
  return {
    id: eventRef.id,
    ...eventData,
  };
}

/**
 * Load all events for a group's calendar.
 * Returns an array of event objects with id field.
 */
export async function getGroupEvents(groupId) {
  if (!groupId) return [];
  const snap = await getDocs(collection(db, 'groups', groupId, 'events'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/**
 * Delete an event from a group's calendar.
 * Permission check (creator OR admin) happens via Firestore rules.
 */
export async function deleteGroupEvent({ groupId, eventId }) {
  if (!groupId || !eventId) throw new Error('Group ID and event ID are required');
  const ref = doc(db, 'groups', groupId, 'events', eventId);
  await deleteDoc(ref);
}
// =====================================================================
// JOIN HELPERS — pending join requests via invite link
// =====================================================================

/**
 * Create or revive a pending member doc when a user clicks an invite link.
 * Three cases:
 *   1) Doc doesn't exist → create new pending doc
 *   2) Doc exists with status='active' → already a member, no-op
 *   3) Doc exists with status='rejected' → revive as pending (re-request)
 */
export async function joinGroupAsPendingMember({ groupId, userId, autoApprove = false }) {
  if (!groupId || !userId) throw new Error('groupId and userId are required');
  const ref = doc(db, 'groups', groupId, 'members', userId);
  
  const existing = await getDoc(ref);
  if (existing.exists()) {
    const data = existing.data();
    
    // Already an active member — nothing to do
    if (data.status === 'active') {
      return { alreadyMember: true, isAdmin: data.isAdmin || false };
    }
    
    // Previously REMOVED by an admin (kicked from the group for a violation).
    // Removed users CANNOT rejoin via invite link — admins must manually
    // re-add them via the Members tab if they decide to give a second chance.
    // This closes the Play Store moderation loophole where a removed bad actor
    // could simply click the same invite link to rejoin within seconds.
    if (data.status === 'removed') {
      const err = new Error('You were removed from this batch by an admin and cannot rejoin via invite. Please contact an admin if you believe this was a mistake.');
      err.code = 'aangan/removed-by-admin';
      throw err;
    }
    
    // Previously rejected OR previously left voluntarily — revive as pending
    if (data.status === 'rejected' || data.status === 'left') {
      await setDoc(ref, {
        status: 'active',
        approvedAt: autoApprove ? serverTimestamp() : null,
        isAdmin: false,
        reRequestedAt: serverTimestamp(),
      }, { merge: true });
      return { alreadyMember: false, reRequest: true };
    }
  }
  
  // Brand new join request
  await setDoc(ref, {
    joinedAt: serverTimestamp(),
    approvedAt: autoApprove ? serverTimestamp() : null,
    viaCode: true,
    approvalDeadline: null,
    isAdmin: false,
    status: 'active',
  });
  return { alreadyMember: false, reRequest: false };
}

/**
 * Add a groupId to the user's memberOfGroupIds array so the group appears
 * in their batches list (with "awaiting approval" banner if not yet approved).
 */
export async function addGroupToUserMembership({ userId, groupId }) {
  if (!userId || !groupId) throw new Error('userId and groupId are required');
  await setDoc(doc(db, 'users', userId), {
    memberOfGroupIds: arrayUnion(groupId),
  }, { merge: true });
}

// =====================================================================
// ADMIN MODERATION HELPERS — pending member approval/rejection
// =====================================================================

/**
 * Fetch all pending members of a group (those with approvedAt: null).
 * Returns array with member doc data + user profile data merged.
 * Admin-only — Firestore rules enforce read access.
 */
export async function getPendingMembers(groupId) {
  if (!groupId) throw new Error('groupId required');
  
  const membersRef = collection(db, 'groups', groupId, 'members');
  const q = query(membersRef, where('approvedAt', '==', null), where('status', '==', 'active'));
  const snap = await getDocs(q);
  
  // For each pending member, also fetch their user profile so admin sees name/photo
  const pendingList = [];
  for (const memberDoc of snap.docs) {
    const memberData = memberDoc.data();
    const userId = memberDoc.id;
    
    let userProfile = null;
    try {
      const userSnap = await getDoc(doc(db, 'users', userId));
      if (userSnap.exists()) {
        userProfile = userSnap.data();
      }
    } catch (e) {
      console.warn('Could not load profile for pending member:', userId, e);
    }
    
    pendingList.push({
      userId,
      joinedAt: memberData.joinedAt,
      viaCode: memberData.viaCode || false,
      // User profile fields (may be null if profile incomplete)
      name: userProfile?.name || 'Unknown user',
      photoUrl: userProfile?.photoUrl || null,
      currentCity: userProfile?.currentCity || '',
      bio: userProfile?.bio || '',
      // Computed initials for fallback avatar
      initials: (userProfile?.name || 'U').split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase().slice(0, 2) || 'U',
    });
  }
  
  // Sort newest first (largest joinedAt)
  pendingList.sort((a, b) => {
    const aTime = a.joinedAt?.toMillis?.() || 0;
    const bTime = b.joinedAt?.toMillis?.() || 0;
    return bTime - aTime;
  });
  
  return pendingList;
}

/**
 * Subscribe to pending members of a group in real-time. Yields the same
 * profile-merged shape as getPendingMembers so callers can swap the one-
 * shot fetch for this subscription without changing any downstream code.
 *
 * Query is identical to getPendingMembers (approvedAt==null + status==active),
 * so the existing composite index covers it — no firestore.indexes.json
 * change required.
 *
 * Admin-only — Firestore rules enforce read access. permission-denied
 * errors are swallowed silently (expected when the caller stops being an
 * admin mid-session, e.g. was demoted).
 *
 * Cost: 1 read per pending member on first callback + 1 read per user
 * profile + 1 read per subsequent change. Pending lists are typically tiny
 * (0–10 docs), so the overhead is negligible.
 *
 * Returns the unsubscribe function.
 */
export function subscribeToPendingMembers(groupId, callback) {
  if (!groupId) return () => {};

  const membersRef = collection(db, 'groups', groupId, 'members');
  const q = query(
    membersRef,
    where('approvedAt', '==', null),
    where('status', '==', 'active'),
  );

  return onSnapshot(q,
    async (snap) => {
      // Fetch user profiles for each pending member so admin sees name/photo.
      // Shape mirrors getPendingMembers exactly so existing pendingJoins
      // consumers (Chats→Requests renderer, admin-queue screen) work unchanged.
      const pendingList = [];
      for (const memberDoc of snap.docs) {
        const memberData = memberDoc.data();
        const userId = memberDoc.id;

        let userProfile = null;
        try {
          const userSnap = await getDoc(doc(db, 'users', userId));
          if (userSnap.exists()) {
            userProfile = userSnap.data();
          }
        } catch (e) {
          console.warn('Could not load profile for pending member:', userId, e);
        }

        pendingList.push({
          userId,
          joinedAt: memberData.joinedAt,
          viaCode: memberData.viaCode || false,
          name: userProfile?.name || 'Unknown user',
          photoUrl: userProfile?.photoUrl || null,
          currentCity: userProfile?.currentCity || '',
          bio: userProfile?.bio || '',
          initials: (userProfile?.name || 'U').split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase().slice(0, 2) || 'U',
        });
      }

      // Newest first — same sort as getPendingMembers so the Requests tab
      // shows most-recent join at the top.
      pendingList.sort((a, b) => {
        const aTime = a.joinedAt?.toMillis?.() || 0;
        const bTime = b.joinedAt?.toMillis?.() || 0;
        return bTime - aTime;
      });

      callback(pendingList);
    },
    (err) => {
      if (err.code !== 'permission-denied') {
        console.error('Pending members subscription error:', err);
      }
    }
  );
}

/**
 * Approve a pending member — sets approvedAt to server timestamp.
 * Admin-only — Firestore rules enforce write access.
 */
export async function approvePendingMember({ groupId, userId }) {
  if (!groupId || !userId) throw new Error('groupId and userId required');
  const memberRef = doc(db, 'groups', groupId, 'members', userId);
  
  // Check if this is a first-time approval (vs re-approval after reject/leave)
  // so we only bump memberCount once per member.
  const existing = await getDoc(memberRef);
  const wasApprovedBefore = existing.exists() && existing.data().approvedAt != null;
  
  await setDoc(memberRef, {
    approvedAt: serverTimestamp(),
    status: 'active',  // ensure status is active (handles re-approval after reject)
  }, { merge: true });
  
  // Only increment if this is the first approval (prevents double-counting on re-approval)
  if (!wasApprovedBefore) {
    await setDoc(doc(db, 'groups', groupId), {
      memberCount: increment(1),
    }, { merge: true });
  }
}

/**
 * Reject a pending member — deletes their member doc + removes group from their user doc.
 * Note: Firestore rules currently don't allow client deletes of member docs
 * (allow delete: if false). For rejection, we instead update status to 'rejected'.
 * The user's memberOfGroupIds gets cleaned up via Cloud Function later, or
 * the user can manually leave from their side.
 *
 * For MVP: change status to 'rejected'. UI stops showing them as members.
 * They CAN still see they were rejected (their own member doc stays accessible).
 */
export async function rejectPendingMember({ groupId, userId }) {
  if (!groupId || !userId) throw new Error('groupId and userId required');
  const memberRef = doc(db, 'groups', groupId, 'members', userId);
  await setDoc(memberRef, {
    status: 'rejected',
    rejectedAt: serverTimestamp(),
  }, { merge: true });
  
  // Also clean memberOfGroupIds on the user's doc. Without this, a rejected
  // pending joiner still sees the group in their batch list with an
  // "Awaiting admin approval" banner forever — same bug as removeGroupMember.
  // The user can always re-request via the invite link (which revives the
  // member doc to 'active' pending status — see joinGroupAsPendingMember).
  try {
    await removeGroupFromUserMembership({ userId, groupId });
  } catch (e) {
    console.warn('rejectPendingMember: failed to clean memberOfGroupIds (non-fatal):', e);
  }
}
// =====================================================================
// PHOTO UPLOADS — compress in browser, write to Firebase Storage
// All uploads enforce: WebP at 720px max, ~150 KB, with 1-year cache headers
// =====================================================================

/**
 * Upload a user's profile photo.
 * Path: users/{userId}/profile.webp
 * Replaces existing photo if any.
 */
export async function uploadProfilePhoto(userId, file) {
  if (!userId || !file) throw new Error('userId and file required');
  
  const compressed = await compressImage(file);
  const path = `users/${userId}/profile.webp`;
  const photoRef = ref(storage, path);
  
  // Cache for 1 year. Photos at this path get replaced if user updates;
  // we use a cache-buster (?t=timestamp) on the URL when reading.
  // customMetadata.ownerUid lets onPhotoUpload Cloud Function credit
  // the right user without a Firestore lookup (also avoids races).
  await uploadBytes(photoRef, compressed, {
    contentType: 'image/webp',
    cacheControl: 'public, max-age=31536000, immutable',
    customMetadata: { ownerUid: userId },
  });
  
  return await getDownloadURL(photoRef);
}

/**
 * Upload a group's cover photo (admin only — rules enforce).
 * Path: groups/{groupId}/cover.webp
 */
export async function uploadGroupCover(groupId, file, uploaderUid) {
  if (!groupId || !file) throw new Error('groupId and file required');
  if (!uploaderUid) throw new Error('uploaderUid required for cover photo metadata');
  
  const compressed = await compressImage(file);
  const path = `groups/${groupId}/cover.webp`;
  const photoRef = ref(storage, path);
  
  // customMetadata.uploaderUid lets onPhotoUpload credit the right user
  // (cover photos count against the uploader's monthly limit, not the group).
  await uploadBytes(photoRef, compressed, {
    contentType: 'image/webp',
    cacheControl: 'public, max-age=31536000, immutable',
    customMetadata: { uploaderUid },
  });
  
  return await getDownloadURL(photoRef);
}
/**
 * Update a group's coverUrl field in Firestore.
 * Should be called AFTER uploadGroupCover() to persist the URL on the group doc.
 */
export async function updateGroupCoverUrl(groupId, coverUrl) {
  if (!groupId) throw new Error('groupId required');
  await setDoc(doc(db, 'groups', groupId), { coverUrl }, { merge: true });
}
/**
 * Update a group's editable fields (name, bio).
 * Admin-only — Firestore rules enforce.
 */
export async function updateGroupInfo(groupId, updates) {
  if (!groupId) throw new Error('groupId required');
  const allowed = {};
  if (typeof updates.name === 'string') {
    allowed.name = updates.name.trim();
    allowed.searchKey = normalizeSearchKey(updates.name);  // keep search index in sync
  }
  if (typeof updates.bio === 'string') allowed.bio = updates.bio.trim();
  if (Object.keys(allowed).length === 0) return;
  await setDoc(doc(db, 'groups', groupId), allowed, { merge: true });
}

/**
 * Upload a wall post photo.
 * Path: groups/{groupId}/posts/{postId}.webp
 * postId can be a freshly-generated Firestore doc ID or a UUID.
 */
export async function uploadPostPhoto(groupId, postId, file, authorUid) {
  if (!groupId || !postId || !file) throw new Error('groupId, postId and file required');
  if (!authorUid) throw new Error('authorUid required for post photo metadata');
  
  // Detect aspect ratio BEFORE compression (uses original dimensions)
  const aspect = await detectAspectRatio(file);
  
  const compressed = await compressImage(file);
  const path = `groups/${groupId}/posts/${postId}.webp`;
  const photoRef = ref(storage, path);
  
  // customMetadata.authorUid is read by onPhotoUpload Cloud Function so it
  // can credit the author's monthly counter immediately, without waiting
  // for the post doc to be written (which happens AFTER this upload returns).
  await uploadBytes(photoRef, compressed, {
    contentType: 'image/webp',
    cacheControl: 'public, max-age=31536000, immutable',
    customMetadata: { authorUid },
  });
  
  const url = await getDownloadURL(photoRef);
  return { url, aspect };
}

/**
 * Delete a profile photo (e.g., when user clears their picture).
 * Failure isn't fatal — log and continue.
 */
export async function deleteProfilePhoto(userId) {
  try {
    await deleteObject(ref(storage, `users/${userId}/profile.webp`));
  } catch (e) {
    if (e.code !== 'storage/object-not-found') {
      console.warn('Failed to delete profile photo:', e);
    }
  }
}
/**
 * Check if a specific user is approved in a specific group.
 * Returns { isMember, isApproved, isAdmin, viaCode, status }
 * status can be: 'active' | 'rejected' | 'none'
 */
export async function getMyMembership(groupId, userId) {
  if (!groupId || !userId) return { isMember: false, isApproved: false, isAdmin: false, viaCode: false, status: 'none' };
  try {
    const memberSnap = await getDoc(doc(db, 'groups', groupId, 'members', userId));
    if (!memberSnap.exists()) return { isMember: false, isApproved: false, isAdmin: false, viaCode: false, status: 'none' };
    const data = memberSnap.data();
    const status = data.status || 'none';
    if (status !== 'active') {
      return { isMember: false, isApproved: false, isAdmin: false, viaCode: false, status };
    }
    return {
      isMember: true,
      isApproved: data.approvedAt != null,
      isAdmin: data.isAdmin || false,
      viaCode: data.viaCode || false,
      status: 'active',
    };
  } catch (e) {
    console.warn('Failed to check membership:', e);
    return { isMember: false, isApproved: false, isAdmin: false, viaCode: false, status: 'none' };
  }
}
/**
 * Remove a group from a user's memberOfGroupIds array.
 * Used when a user is rejected from a group (so they don't see it in their batch list anymore).
 */
export async function removeGroupFromUserMembership({ userId, groupId }) {
  if (!userId || !groupId) throw new Error('userId and groupId required');
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) return;
    const ids = userSnap.data().memberOfGroupIds || [];
    const filtered = ids.filter(id => id !== groupId);
    await setDoc(userRef, { memberOfGroupIds: filtered }, { merge: true });
  } catch (e) {
    console.warn('Failed to remove group from user membership:', e);
  }
}
/**
 * Load a single user's profile by UID.
 * Returns minimal display data: { id, name, photoUrl, currentCity, color, initials, bio, status }
 * Returns null if user doc doesn't exist.
 */
export async function loadUserProfile(userId) {
  if (!userId) return null;
  try {
    const snap = await getDoc(doc(db, 'users', userId));
    if (!snap.exists()) return null;
    const data = snap.data();
    const name = data.name || 'User';
    const initials = name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(p => p[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U';
    return {
      id: userId,
      name,
      photoUrl: data.photoUrl || null,
      currentCity: data.currentCity || '',
      bio: data.bio || '',
      status: data.status || '',
      profession: data.profession || '',
      color: data.color || '#C45A3F',
      initials,
    };
  } catch (e) {
    console.warn('Failed to load user profile:', userId, e);
    return null;
  }
}
// =====================================================================
// WALL POSTS — Firestore-persisted posts in a group
// Path: /groups/{groupId}/posts/{postId}
// =====================================================================

/**
 * Create a new wall post in a group.
 * Returns the new post object with id + serverTimestamp resolved.
 *
 * If postId is provided (e.g. you generated it for photo upload), it's used.
 * Otherwise Firestore auto-generates one.
 */
export async function createPost({ groupId, authorId, text, photo, postId = null, subTab = 'wall', category = null }) {
  if (!groupId || !authorId) throw new Error('groupId and authorId required');
  if (!text?.trim() && !photo) throw new Error('Post must have text or photo');

  const data = {
    authorId,
    text: text?.trim() || '',
    photo: photo || null,
    subTab,                       // 'wall' | 'picks'
    category: category || null,   // optional, for picks: 'job' | 'rec' | 'heads-up' | 'other'
    createdAt: serverTimestamp(),
    likeCount: 0,
    commentCount: 0,
  };

  if (postId) {
    // Use the supplied ID (so it matches the photo path)
    await setDoc(doc(db, 'groups', groupId, 'posts', postId), data);
    return { id: postId, ...data };
  } else {
    const ref = await addDoc(collection(db, 'groups', groupId, 'posts'), data);
    return { id: ref.id, ...data };
  }
}

/**
 * Subscribe to posts in a group, latest first, with a page size.
 * Returns the unsubscribe function — call it when you don't need updates.
 *
 * Usage:
 *   const unsub = subscribeToGroupPosts(groupId, 30, (posts) => setPosts(posts));
 *   // Later:
 *   unsub();
 */
export function subscribeToGroupPosts(groupId, pageSize, callback) {
  if (!groupId) return () => {};
  const q = query(
    collection(db, 'groups', groupId, 'posts'),
    orderBy('createdAt', 'desc'),
    limit(pageSize)
  );
  return onSnapshot(q,
    (snap) => {
      const posts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      callback(posts);
    },
    (err) => {
      // Silent unless it's an unexpected error
      if (err.code !== 'permission-denied') {
        console.error('Posts subscription error:', err);
      }
    }
  );
}

/**
 * Delete a post (and its photo if any).
 * Caller must verify the user is the author OR an admin (via Firestore rules).
 */
export async function deletePost({ groupId, postId, photoUrl }) {
  if (!groupId || !postId) throw new Error('groupId and postId required');

  // Delete the post doc
  await deleteDoc(doc(db, 'groups', groupId, 'posts', postId));

  // Delete the photo from Storage if it exists
  if (photoUrl) {
    try {
      // Path is groups/{groupId}/posts/{postId}.webp
      await deleteObject(ref(storage, `groups/${groupId}/posts/${postId}.webp`));
    } catch (e) {
      if (e.code !== 'storage/object-not-found') {
        console.warn('Photo delete failed (non-fatal):', e);
      }
    }
  }
}

/**
 * Toggle like on a post — adds or removes /likes/{userId} doc and updates likeCount.
 * Returns the new liked state (true = liked, false = unliked).
 *
 * Note: We use individual subdocs (not arrays) so likes scale to 1000+ without doc-size limits.
 */
export async function togglePostLike({ groupId, postId, userId }) {
  if (!groupId || !postId || !userId) throw new Error('All three required');
  const likeRef = doc(db, 'groups', groupId, 'posts', postId, 'likes', userId);
  const postRef = doc(db, 'groups', groupId, 'posts', postId);

  const existing = await getDoc(likeRef);
  if (existing.exists()) {
    // Unlike
    await deleteDoc(likeRef);
    await setDoc(postRef, { likeCount: increment(-1) }, { merge: true });
    return false;
  } else {
    // Like
    await setDoc(likeRef, { likedAt: serverTimestamp() });
    await setDoc(postRef, { likeCount: increment(1) }, { merge: true });
    return true;
  }
}

/**
 * Check if the current user has liked a post.
 * Useful when loading posts (we don't get this for free in subscription).
 */
export async function hasUserLikedPost({ groupId, postId, userId }) {
  if (!groupId || !postId || !userId) return false;
  try {
    const snap = await getDoc(doc(db, 'groups', groupId, 'posts', postId, 'likes', userId));
    return snap.exists();
  } catch (e) {
    return false;
  }
}

/**
 * Add a comment to a post.
 * Increments commentCount on the post atomically.
 */
export async function addPostComment({ groupId, postId, authorId, text }) {
  if (!groupId || !postId || !authorId) throw new Error('All required');
  if (!text?.trim()) throw new Error('Comment cannot be empty');

  const data = {
    authorId,
    text: text.trim(),
    createdAt: serverTimestamp(),
  };

  const commentRef = await addDoc(collection(db, 'groups', groupId, 'posts', postId, 'comments'), data);
  // Increment commentCount on the post
  await setDoc(doc(db, 'groups', groupId, 'posts', postId), { commentCount: increment(1) }, { merge: true });

  return { id: commentRef.id, ...data };
}

/**
 * Edit an existing comment's text. Only the author can edit (rules enforce).
 * Sets `editedAt` server timestamp so the UI can show an "edited" label.
 */
export async function editPostComment({ groupId, postId, commentId, newText }) {
  if (!groupId || !postId || !commentId) throw new Error('All IDs required');
  if (!newText?.trim()) throw new Error('Comment cannot be empty');
  await setDoc(
    doc(db, 'groups', groupId, 'posts', postId, 'comments', commentId),
    {
      text: newText.trim(),
      editedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

/**
 * Subscribe to comments on a post.
 * Returns the unsubscribe function.
 */

/**
 * Subscribe to LATEST N comments on a post (for cost protection).
 * Returns the unsubscribe function.
 *
 * Note: Firestore query limits "newest N" via orderBy desc + limit.
 * We then reverse client-side to display oldest-first in UI.
 */
export function subscribeToPostComments(groupId, postId, pageSize, callback) {
  if (!groupId || !postId) return () => {};
  const q = query(
    collection(db, 'groups', groupId, 'posts', postId, 'comments'),
    orderBy('createdAt', 'desc'),
    limit(pageSize)
  );
  return onSnapshot(q,
    (snap) => {
      // Newest first — UI displays top-down
      const comments = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      callback(comments);
    },
    (err) => {
      if (err.code !== 'permission-denied') {
        console.error('Comments subscription error:', err);
      }
    }
  );
}

/**
 * Load older comments before a given timestamp (one-shot, not subscription).
 * Used by "View earlier comments" button.
 * Returns array of older comments + hasMore flag.
 */
export async function loadOlderComments({ groupId, postId, beforeTimestamp, pageSize = 10 }) {
  if (!groupId || !postId || !beforeTimestamp) return { comments: [], hasMore: false };
  
  const q = query(
    collection(db, 'groups', groupId, 'posts', postId, 'comments'),
    orderBy('createdAt', 'desc'),
    where('createdAt', '<', beforeTimestamp),
    limit(pageSize + 1)  // fetch 1 extra to detect "has more"
  );
  
  try {
    const snap = await getDocs(q);
    const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const hasMore = docs.length > pageSize;
    const comments = docs.slice(0, pageSize); // newest first within this older batch
    return { comments, hasMore };
  } catch (e) {
    console.error('Failed to load older comments:', e);
    return { comments: [], hasMore: false };
  }
}

/**
 * Delete a comment (author or admin).
 * Decrements commentCount.
 */
export async function deletePostComment({ groupId, postId, commentId }) {
  if (!groupId || !postId || !commentId) throw new Error('All required');
  await deleteDoc(doc(db, 'groups', groupId, 'posts', postId, 'comments', commentId));
  await setDoc(doc(db, 'groups', groupId, 'posts', postId), { commentCount: increment(-1) }, { merge: true });
}
/**
 * Finds groups where the user is an active member but missing from their memberOfGroupIds array.
 * This is a self-healing function — if a group join didn't fully sync (rare bug),
 * this finds the group and adds it back.
 *
 * Strategy: Use a collection group query to find all member docs across all groups
 * where the user is the document ID and status='active'.
 *
 * Returns array of group IDs that need to be added to the user doc.
 */
export async function findOrphanedMemberships(userId) {
  if (!userId) return [];
  
  try {
    // Get user's current memberOfGroupIds
    const userSnap = await getDoc(doc(db, 'users', userId));
    const knownGroupIds = userSnap.exists() ? (userSnap.data().memberOfGroupIds || []) : [];
    
    // Use a collection group query to find ALL member docs for this user across ALL groups
    // (Firestore syntax: collectionGroup queries scan all subcollections named 'members')
    const { collectionGroup } = await import('firebase/firestore');
    const memberQuery = query(
      collectionGroup(db, 'members'),
      where('status', '==', 'active')
    );
    const memberSnap = await getDocs(memberQuery);
    
    // For each member doc where the user is the document ID, extract the group ID
    const groupsImAMemberOf = [];
    memberSnap.forEach(d => {
      if (d.id === userId) {
        // The doc path is groups/{groupId}/members/{userId}
        // d.ref.parent is members collection, d.ref.parent.parent is the group doc
        const groupId = d.ref.parent.parent?.id;
        if (groupId) groupsImAMemberOf.push(groupId);
      }
    });
    
    // Find groups in groupsImAMemberOf that aren't in knownGroupIds
    const orphaned = groupsImAMemberOf.filter(gid => !knownGroupIds.includes(gid));
    return orphaned;
  } catch (e) {
    console.warn('Could not check for orphaned memberships:', e);
    return [];
  }
}
// =====================================================================
// CHAT — 1-on-1 messaging persisted to Firestore
// Path: /chats/{chatId}                  — parent doc with metadata
//       /chats/{chatId}/messages/{msgId} — individual messages
//
// chatId is deterministic: sorted UIDs joined with "_" so the same two
// users always map to the same chat doc, regardless of who initiated.
//
// COST PROTECTION:
//   - Chat list: one-shot load, no real-time subscription
//   - Active chat: subscribe to LAST 10 messages only
//   - "View earlier": one-shot fetch of next 10 older messages
// =====================================================================

/**
 * Build a deterministic chat ID from two user UIDs.
 * Sort UIDs alphabetically and join with "_" so chat({a,b}) === chat({b,a}).
 */
export function makeChatId(uid1, uid2) {
  if (!uid1 || !uid2) throw new Error('Both UIDs required');
  if (uid1 === uid2) throw new Error('Cannot chat with yourself');
  const [a, b] = [uid1, uid2].sort();
  return `${a}_${b}`;
}

/**
 * Load all chats the user is a participant in.
 * One-shot load — used for initial chat list, also kept as fallback.
 * 
 * Returns array of chat docs with their IDs.
 */
export async function loadUserChats(userId) {
  if (!userId) return [];
  
  try {
    const q = query(
      collection(db, 'chats'),
      where('participants', 'array-contains', userId),
      orderBy('lastMessageAt', 'desc'),
      limit(50)  // cap chat list at 50 most recent
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.warn('Failed to load user chats:', e);
    return [];
  }
}

/**
 * Subscribe to user's chat list in real-time.
 * Returns the unsubscribe function.
 *
 * Cost: 1 read per chat doc on initial load + 1 per change.
 * For 50 chats, ~50 reads on first subscribe, then incremental.
 *
 * Why subscribe instead of polling: when someone sends you a message,
 * the lastMessage/lastMessageAt on the chat doc updates. Without a
 * subscription, the chat list in the UI wouldn't reflect that until
 * the user signs out/in.
 */
export function subscribeToUserChats(userId, callback) {
  if (!userId) return () => {};
  
  const q = query(
    collection(db, 'chats'),
    where('participants', 'array-contains', userId),
    orderBy('lastMessageAt', 'desc'),
    limit(50)
  );
  
  return onSnapshot(q,
    (snap) => {
      const chats = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      callback(chats);
    },
    (err) => {
      if (err.code !== 'permission-denied') {
        console.error('User chats subscription error:', err);
      }
    }
  );
}

/**
 * Subscribe to the latest N messages in a chat.
 * Returns the unsubscribe function.
 *
 * Strategy: orderBy 'sentAt' descending + limit(N) gives us the latest N.
 * We don't reverse — UI displays newest at bottom (chat-style) by mapping
 * the array order in the component.
 */
export function subscribeToLatestMessages(chatId, pageSize, callback, clearedAt = null) {
  if (!chatId) return () => {};
  
  // If user has a clearedAt timestamp, only show messages newer than it
  const constraints = [orderBy('sentAt', 'desc'), limit(pageSize)];
  if (clearedAt) {
    constraints.unshift(where('sentAt', '>', clearedAt));
  }
  
  const q = query(collection(db, 'chats', chatId, 'messages'), ...constraints);
  return onSnapshot(q,
    (snap) => {
      // Reverse to display oldest → newest (top to bottom, chat-style)
      const messages = snap.docs.map(d => ({ id: d.id, ...d.data() })).reverse();
      callback(messages);
    },
    (err) => {
      if (err.code !== 'permission-denied') {
        console.error('Messages subscription error:', err);
      }
    }
  );
}

/**
 * Send a chat message.
 * 
 * Writes to:
 *   /chats/{chatId}/messages/{auto-id}     — the message itself
 *   /chats/{chatId}                        — updates lastMessage, lastMessageAt
 *
 * If the chat doc doesn't exist yet, creates it (first-time chat).
 * The first user to send creates the chat with both as participants.
 */
export async function sendChatMessage({ chatId, fromId, toId, text, imageUrl }) {
  if (!chatId || !fromId || !toId) throw new Error('chatId, fromId, toId required');
  // Phase 2.3 C4 — photo messages allowed: require text OR imageUrl, not
  // both empty. Existing text-only callers are unchanged (imageUrl is
  // optional and undefined when not passed). This is the only V1 touch
  // C4 needs — the rest of V1 sendChatMessage logic stays identical.
  const trimmedText = (text || '').trim();
  if (!trimmedText && !imageUrl) {
    throw new Error('Message must have text or a photo');
  }

  const chatRef = doc(db, 'chats', chatId);
  const participants = [fromId, toId].sort();
  // lastMessage preview shown in chat list / notifications. Use the text
  // when present; otherwise the photo placeholder so the recipient sees
  // a meaningful preview rather than an empty bubble.
  const lastMessagePreview = trimmedText || '📷 Photo';

  // Use setDoc with merge:true so the same call works whether doc exists or not.
  // The Firestore rules allow this because:
  //   - If doc exists: update rule checks isParticipant() against existing data
  //   - If doc doesn't exist: create rule checks willBeParticipant() against new data
  // Both paths require the user to be one of the participants.
  //
  // ALSO: when sending a message, mark the chat as "read" for the SENDER.
  // The sender obviously doesn't have unread messages they themselves just sent.
  // We use dot-path notation so we only update lastReadAt[fromId], not the whole map.
  await setDoc(chatRef, {
    participants,
    requesterId: fromId,
    acceptedAt: serverTimestamp(),
    lastMessage: lastMessagePreview,
    lastMessageAt: serverTimestamp(),
    lastReadAt: { [fromId]: serverTimestamp() },
  }, { merge: true });

  // Add the message. text is always written (even if empty) for shape
  // consistency; imageUrl is only written when present, so legacy
  // text-only messages don't carry a meaningless imageUrl: null field.
  const messageData = {
    fromId,
    text: trimmedText,
    sentAt: serverTimestamp(),
    ...(imageUrl ? { imageUrl } : {}),
  };
  const msgRef = await addDoc(collection(db, 'chats', chatId, 'messages'), messageData);

  return { id: msgRef.id, ...messageData };
}

/**
 * Load older messages before a given timestamp (one-shot).
 * Used by "View earlier messages" button.
 * Returns { messages, hasMore }.
 */
export async function loadOlderMessages({ chatId, beforeTimestamp, pageSize = 10, clearedAt = null }) {
  if (!chatId || !beforeTimestamp) return { messages: [], hasMore: false };
  
  try {
    // Build query constraints — clearedAt filters out messages from before user's clear
    const constraints = [
      orderBy('sentAt', 'desc'),
      where('sentAt', '<', beforeTimestamp),
      limit(pageSize + 1),
    ];
    if (clearedAt) {
      constraints.push(where('sentAt', '>', clearedAt));
    }
    
    const q = query(collection(db, 'chats', chatId, 'messages'), ...constraints);
    const snap = await getDocs(q);
    const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const hasMore = docs.length > pageSize;
    // Reverse to oldest → newest within this older batch
    const messages = docs.slice(0, pageSize).reverse();
    return { messages, hasMore };
  } catch (e) {
    console.error('Failed to load older messages:', e);
    return { messages: [], hasMore: false };
  }
}
/**
 * Mark a chat as read for a specific user.
 * Updates lastReadAt[userId] on the chat doc to the current server time.
 * Called when the user opens a chat — all messages older than this timestamp
 * are considered "read" by this user.
 *
 * Uses dot-path notation so we only update one field of the lastReadAt map,
 * not overwrite the whole thing (which would clobber the other user's value).
 *
 * Non-fatal on failure — logged and swallowed. Worst case: chat shows unread
 * for one extra second until the next subscription tick.
 */
export async function markChatRead({ chatId, userId }) {
  if (!chatId || !userId) return;
  try {
    await setDoc(
      doc(db, 'chats', chatId),
      { lastReadAt: { [userId]: serverTimestamp() } },
      { merge: true }
    );
  } catch (e) {
    console.warn('Could not mark chat as read (non-fatal):', e);
  }
}

/**
 * Personal chat deletion (WhatsApp-style).
 * Sets a clearedAt timestamp for the user — messages older than that are hidden
 * for them only. The other user still sees the full conversation.
/**
 * Personal chat deletion (WhatsApp-style).
 * Sets a clearedAt timestamp for the user — messages older than that are hidden
 * for them only. The other user still sees the full conversation.
 * 
 * Implementation: chat doc has a `clearedAt` map: { uid: timestamp }
 * Subscription filter: only show messages where sentAt > clearedAt[myUid]
 * 
 * If BOTH participants have cleared the chat, we remove the doc + messages
 * (cleanup, since nobody can see anything anyway).
 */
export async function clearChatForUser({ chatId, userId }) {
  if (!chatId || !userId) throw new Error('chatId and userId required');
  
  const chatRef = doc(db, 'chats', chatId);
  const chatSnap = await getDoc(chatRef);
  if (!chatSnap.exists()) return; // already gone
  
  const data = chatSnap.data();
  const participants = data.participants || [];
  const clearedAt = data.clearedAt || {};
  
  // Mark this user's clearedAt to now
  const newClearedAt = { ...clearedAt, [userId]: new Date() };
  
  // Check if both participants have now cleared
  const allCleared = participants.every(uid => newClearedAt[uid] != null);
  
  if (allCleared) {
    // Both sides cleared — full cleanup. Delete messages then parent.
    const messagesRef = collection(db, 'chats', chatId, 'messages');
    const PAGE_SIZE = 100;
    while (true) {
      const snap = await getDocs(query(messagesRef, limit(PAGE_SIZE)));
      if (snap.empty) break;
      await Promise.all(snap.docs.map(d => deleteDoc(d.ref)));
      if (snap.size < PAGE_SIZE) break;
    }
    await deleteDoc(chatRef);
  } else {
    // One side cleared — just record their clearedAt timestamp
    await setDoc(chatRef, {
      clearedAt: newClearedAt,
    }, { merge: true });
  }
}

/**
 * Old deleteChat — kept for backward compatibility but routes to clearChatForUser.
 * Caller should migrate to clearChatForUser to be explicit about per-user behavior.
 */
export async function deleteChat(chatId, userId) {
  if (userId) {
    return clearChatForUser({ chatId, userId });
  }
  // Fallback: hard delete (only used by tests/admin tools, not user-facing UI)
  if (!chatId) throw new Error('chatId required');
  const messagesRef = collection(db, 'chats', chatId, 'messages');
  const PAGE_SIZE = 100;
  while (true) {
    const snap = await getDocs(query(messagesRef, limit(PAGE_SIZE)));
    if (snap.empty) break;
    await Promise.all(snap.docs.map(d => deleteDoc(d.ref)));
    if (snap.size < PAGE_SIZE) break;
  }
  await deleteDoc(doc(db, 'chats', chatId));
}
// =====================================================================
// MEMBER LIST — load all approved members of a group
// Used by the Members tab to display batchmates.
// =====================================================================

/**
 * Subscribe to the list of approved members in a group, with their profiles merged in.
 * Returns the unsubscribe function.
 *
 * Cost: 1 read per member doc + 1 read per user profile = ~2N reads on first load.
 * For groups with 100 members this is ~200 reads — well under daily quota.
 */
export function subscribeToGroupMembers(groupId, callback) {
  if (!groupId) return () => {};
  
  const membersRef = collection(db, 'groups', groupId, 'members');
  const q = query(membersRef, where('status', '==', 'active'));
  
  return onSnapshot(q,
    async (snap) => {
      // Filter to only approved members
      const approvedDocs = snap.docs.filter(d => d.data().approvedAt != null);
      
      // Fetch user profiles for each approved member
      const memberList = [];
      for (const memberDoc of approvedDocs) {
        const memberData = memberDoc.data();
        const userId = memberDoc.id;
        
        try {
          const userSnap = await getDoc(doc(db, 'users', userId));
          if (userSnap.exists()) {
            const profile = userSnap.data();
            memberList.push({
              id: userId,
              name: profile.name || 'Member',
              photoUrl: profile.photoUrl || null,
              currentCity: profile.currentCity || '—',
              bio: profile.bio || '',
              status: profile.status || '',
              profession: profile.profession || '',
              color: profile.color || '#C45A3F',
              isAdmin: memberData.isAdmin === true,
              joinedAt: memberData.joinedAt,
              approvedAt: memberData.approvedAt,
              initials: (profile.name || 'M').split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase().slice(0, 2) || 'M',
            });
          }
        } catch (e) {
          console.warn('Could not load profile for member:', userId, e);
        }
      }
      
      callback(memberList);
    },
    (err) => {
      if (err.code !== 'permission-denied') {
        console.error('Members subscription error:', err);
      }
    }
  );
}
// =====================================================================
// TIME CAPSULE — saved memories per group
// Path: /groups/{groupId}/memories/{memoryId}
// Storage: groups/{groupId}/memories/{memoryId}.jpg
//
// V1 limit: 10 memories per group. Enforced at the call site (UI checks
// count before allowing upload). Firestore rules don't enforce this since
// it'd require a separate count doc — fine for V1, can add in V2.
// =====================================================================

/**
 * Subscribe to all memories in a group (real-time).
 * Returns the unsubscribe function.
 *
 * Cost: 1 read per memory + 1 read per change. With 10-memory cap,
 * this is at most ~10 reads per group view.
 */
export function subscribeToGroupMemories(groupId, callback) {
  if (!groupId) return () => {};
  const q = query(
    collection(db, 'groups', groupId, 'memories'),
    orderBy('addedAt', 'desc')
  );
  return onSnapshot(q,
    (snap) => {
      const memories = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      callback(memories);
    },
    (err) => {
      if (err.code !== 'permission-denied') {
        console.error('Memories subscription error:', err);
      }
    }
  );
}

/**
 * Get current memory count in a group (for limit enforcement).
 */
export async function getMemoryCount(groupId) {
  if (!groupId) return 0;
  try {
    const snap = await getDocs(collection(db, 'groups', groupId, 'memories'));
    return snap.size;
  } catch (e) {
    console.warn('Could not count memories:', e);
    return 0;
  }
}

/**
 * Upload a memory photo to Firebase Storage with compression.
 * Returns { url, aspect } — same shape as uploadPostPhoto.
 * Mirrors the post upload pattern exactly.
 */
export async function uploadMemoryPhoto(groupId, memoryId, file, uploaderUid) {
  if (!groupId || !memoryId || !file) throw new Error('groupId, memoryId, file required');
  if (!uploaderUid) throw new Error('uploaderUid required for memory photo metadata');
  
  // Detect aspect ratio BEFORE compression (uses original dimensions)
  const aspect = await detectAspectRatio(file);
  
  const compressed = await compressImage(file);
  const path = `groups/${groupId}/memories/${memoryId}.webp`;
  const photoRef = ref(storage, path);
  
  // customMetadata.uploaderUid lets onPhotoUpload credit the right user
  // immediately, before the memory doc is written to Firestore (race-proof).
  await uploadBytes(photoRef, compressed, {
    contentType: 'image/webp',
    cacheControl: 'public, max-age=31536000, immutable',
    customMetadata: { uploaderUid },
  });
  
  const url = await getDownloadURL(photoRef);
  return { url, aspect };
}

/**
 * Add a new memory to a group's Time Capsule.
 * Photo must already be uploaded — pass the resulting url + aspect.
 */
export async function addGroupMemory({ groupId, memoryId, addedById, photoUrl, label, aspect }) {
  if (!groupId || !memoryId || !addedById) throw new Error('Required fields missing');
  
  const memoryData = {
    photoUrl,
    label: label || 'Untitled memory',
    aspect: aspect || 'square',
    addedById,
    addedAt: serverTimestamp(),
  };
  
  await setDoc(doc(db, 'groups', groupId, 'memories', memoryId), memoryData);
  return { id: memoryId, ...memoryData };
}

/**
 * Delete a memory + its photo from Storage.
 * Caller must verify they're the author or an admin (rules enforce this too).
 */
export async function deleteGroupMemory({ groupId, memoryId, photoUrl }) {
  if (!groupId || !memoryId) throw new Error('groupId, memoryId required');
  
  // Delete the doc first
  await deleteDoc(doc(db, 'groups', groupId, 'memories', memoryId));
  
  // Then try to delete the storage file (best-effort, non-blocking on failure)
  if (photoUrl) {
    try {
      const path = `groups/${groupId}/memories/${memoryId}.webp`;
      const storageRef = ref(storage, path);
      await deleteObject(storageRef);
    } catch (e) {
      if (e.code !== 'storage/object-not-found') {
        console.warn('Could not delete memory photo from storage (non-fatal):', e);
      }
    }
  }
}
// =====================================================================
// LEAVE GROUP — user removes themselves from a group
// Updates member doc status to 'left' AND removes group from user's
// memberOfGroupIds array so it disappears from their batch list.
//
// If the user is the SOLE admin, we block — they need to assign another
// admin first (handled at the UI level).
// =====================================================================

export async function leaveGroup({ groupId, userId }) {
  if (!groupId || !userId) throw new Error('groupId and userId required');
  
  // Check if user was an approved member before leaving (only decrement count if so)
  const memberRef = doc(db, 'groups', groupId, 'members', userId);
  const existing = await getDoc(memberRef);
  const wasApproved = existing.exists() && existing.data().approvedAt != null && existing.data().status === 'active';
  
  // Step 1: mark the member doc as 'left'. Self-write on own member doc,
  // allowed by firestore.rules. If this throws, the whole leave bails
  // cleanly — no partial state where memberOfGroupIds gets cleaned but
  // the member doc still says status='active'.
  await setDoc(memberRef, {
    status: 'left',
    leftAt: serverTimestamp(),
  }, { merge: true });
  
  // Step 2: remove the group from user.memberOfGroupIds. Self-write on
  // own user doc, allowed by firestore.rules. Runs BEFORE the group root-
  // doc write below so a permission-denied on that write doesn't block
  // this cleanup — which was the previous bug: non-admin leaves left
  // memberOfGroupIds stale, so the group re-appeared on next app boot.
  await removeGroupFromUserMembership({ userId, groupId });
  
  // Step 3: best-effort memberCount decrement. Writes the group root doc,
  // which firestore.rules restricts to isGroupAdmin — succeeds for admins
  // leaving, fails permission-denied for regular members. We swallow the
  // failure so the leave completes successfully from the user's perspective.
  // Counter drift for non-admin leaves is a known cost; the correct long-
  // term fix is a Cloud Function on member.status transitions doing the
  // decrement server-side with admin privileges. Tracked as pending polish.
  if (wasApproved) {
    try {
      await setDoc(doc(db, 'groups', groupId), {
        memberCount: increment(-1),
      }, { merge: true });
    } catch (err) {
      if (err?.code !== 'permission-denied') {
        console.warn('memberCount decrement failed on leave:', err);
      }
      // permission-denied is expected for non-admin leaves — silent.
    }
  }
}

/**
 * Promote a member to admin.
 * Only existing admins can call this (Firestore rules enforce).
 * Targets must already be an active approved member.
 */
export async function makeGroupAdmin({ groupId, userId }) {
  if (!groupId || !userId) throw new Error('groupId and userId required');
  const memberRef = doc(db, 'groups', groupId, 'members', userId);
  await setDoc(memberRef, {
    isAdmin: true,
    promotedAt: serverTimestamp(),
  }, { merge: true });
  // Also update the group's adminIds array so the field reflects truth
  await setDoc(doc(db, 'groups', groupId), {
    adminIds: arrayUnion(userId),
  }, { merge: true });
}

/**
 * Remove a member from a group (admin action).
 * Sets member.status = 'removed'. Decrements memberCount.
 * Member's profile and posts stay, but they lose access to the batch.
 * Only existing admins can call this (Firestore rules enforce).
 */
export async function removeGroupMember({ groupId, userId }) {
  if (!groupId || !userId) throw new Error('groupId and userId required');
  
  const memberRef = doc(db, 'groups', groupId, 'members', userId);
  const existing = await getDoc(memberRef);
  const wasApproved = existing.exists() && existing.data().approvedAt != null && existing.data().status === 'active';
  
  // 1. Mark the membership as removed (status flips so they can't read group content)
  await setDoc(memberRef, {
    status: 'removed',
    removedAt: serverTimestamp(),
  }, { merge: true });
  
  // 2. Decrement the group's memberCount if they were a counted member
  if (wasApproved) {
    await setDoc(doc(db, 'groups', groupId), {
      memberCount: increment(-1),
    }, { merge: true });
  }
  
  // 3. CRITICAL: also remove the group from the user's memberOfGroupIds array.
  // Without this, the removed user still sees the group in their batch list
  // on next refresh (with an "Awaiting admin approval" banner — wrong UX,
  // confusing for the user). The leaveGroup flow already does this; remove
  // must too so the user-facing experience is identical to leaving voluntarily.
  // Best-effort: if this fails, the rules-level removal still holds; this is
  // just UI cleanup.
  try {
    await removeGroupFromUserMembership({ userId, groupId });
  } catch (e) {
    console.warn('removeGroupMember: failed to clean memberOfGroupIds (non-fatal):', e);
  }
}

/**
 * Check if user is the only admin of a group.
 * Used to prevent leaving when no other admins exist.
 */
export async function isOnlyAdmin({ groupId, userId }) {
  if (!groupId || !userId) return false;
  try {
    const membersRef = collection(db, 'groups', groupId, 'members');
    const q = query(membersRef, where('isAdmin', '==', true), where('status', '==', 'active'));
    const snap = await getDocs(q);
    if (snap.size <= 1 && snap.docs.some(d => d.id === userId)) return true;
    return false;
  } catch (e) {
    console.warn('Could not check admin count:', e);
    return false;
  }
}
// =====================================================================
// PHOTO LIMITS — monthly counters per user / per group
// 
// Strategy: store { yyyymm, count } maps. If yyyymm doesn't match current
// month, treat as zero. Caller checks BEFORE upload, increments AFTER.
//
// V1 enforcement is client-side only. Server-side limits = V2 (Cloud Functions).
// =====================================================================

function currentYYYYMM() {
  const now = new Date();
  return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Check if a user is under their post-photo monthly limit.
 * Returns { allowed: bool, used: number, limit: number, remaining: number }
 */
export async function checkPostPhotoLimit(userId, limit = 30) {
  if (!userId) return { allowed: false, used: 0, limit, remaining: 0 };
  try {
    const snap = await getDoc(doc(db, 'users', userId));
    const data = snap.exists() ? snap.data() : {};
    const counter = data.postPhotosThisMonth || {};
    const ym = currentYYYYMM();
    const used = (counter.yyyymm === ym) ? (counter.count || 0) : 0;
    return {
      allowed: used < limit,
      used,
      limit,
      remaining: Math.max(0, limit - used),
    };
  } catch (e) {
    console.warn('Could not check post photo limit:', e);
    return { allowed: true, used: 0, limit, remaining: limit }; // fail open
  }
}

/**
 * Increment a user's post-photo counter.
 * Call AFTER a successful upload.
 */
export async function bumpPostPhotoCount(userId) {
  if (!userId) return;
  try {
    const ref = doc(db, 'users', userId);
    const snap = await getDoc(ref);
    const data = snap.exists() ? snap.data() : {};
    const counter = data.postPhotosThisMonth || {};
    const ym = currentYYYYMM();
    const newCount = (counter.yyyymm === ym) ? (counter.count || 0) + 1 : 1;
    await setDoc(ref, {
      postPhotosThisMonth: { yyyymm: ym, count: newCount },
    }, { merge: true });
  } catch (e) {
    console.warn('Could not bump post photo count:', e);
  }
}

/**
 * Check if user is under their profile-photo monthly change limit.
 */
export async function checkProfilePhotoLimit(userId, limit = 5) {
  if (!userId) return { allowed: false, used: 0, limit, remaining: 0 };
  try {
    const snap = await getDoc(doc(db, 'users', userId));
    const data = snap.exists() ? snap.data() : {};
    const counter = data.profilePhotosThisMonth || {};
    const ym = currentYYYYMM();
    const used = (counter.yyyymm === ym) ? (counter.count || 0) : 0;
    return {
      allowed: used < limit,
      used,
      limit,
      remaining: Math.max(0, limit - used),
    };
  } catch (e) {
    console.warn('Could not check profile photo limit:', e);
    return { allowed: true, used: 0, limit, remaining: limit };
  }
}

export async function bumpProfilePhotoCount(userId) {
  if (!userId) return;
  try {
    const ref = doc(db, 'users', userId);
    const snap = await getDoc(ref);
    const data = snap.exists() ? snap.data() : {};
    const counter = data.profilePhotosThisMonth || {};
    const ym = currentYYYYMM();
    const newCount = (counter.yyyymm === ym) ? (counter.count || 0) + 1 : 1;
    await setDoc(ref, {
      profilePhotosThisMonth: { yyyymm: ym, count: newCount },
    }, { merge: true });
  } catch (e) {
    console.warn('Could not bump profile photo count:', e);
  }
}

/**
 * Check if a group is under its cover-change monthly limit.
 * (Tracked on the group doc, not the user.)
 */
export async function checkGroupCoverLimit(groupId, limit = 3) {
  if (!groupId) return { allowed: false, used: 0, limit, remaining: 0 };
  try {
    const snap = await getDoc(doc(db, 'groups', groupId));
    const data = snap.exists() ? snap.data() : {};
    const counter = data.coverChangesThisMonth || {};
    const ym = currentYYYYMM();
    const used = (counter.yyyymm === ym) ? (counter.count || 0) : 0;
    return {
      allowed: used < limit,
      used,
      limit,
      remaining: Math.max(0, limit - used),
    };
  } catch (e) {
    console.warn('Could not check group cover limit:', e);
    return { allowed: true, used: 0, limit, remaining: limit };
  }
}

export async function bumpGroupCoverCount(groupId) {
  if (!groupId) return;
  try {
    const ref = doc(db, 'groups', groupId);
    const snap = await getDoc(ref);
    const data = snap.exists() ? snap.data() : {};
    const counter = data.coverChangesThisMonth || {};
    const ym = currentYYYYMM();
    const newCount = (counter.yyyymm === ym) ? (counter.count || 0) + 1 : 1;
    await setDoc(ref, {
      coverChangesThisMonth: { yyyymm: ym, count: newCount },
    }, { merge: true });
  } catch (e) {
    console.warn('Could not bump group cover count:', e);
  }
}

// =====================================================================
// GROUP SEARCH — find batches by name + city, with fuzzy ranking
// 
// Strategy:
//   1. Firestore query: type + searchKey prefix (cheap, indexed)
//   2. Client-side: filter by city (substring), rank by relevance
//
// Why this works for V1:
//   - Firestore composite index (type, searchKey) makes step 1 instant
//   - Returning ~50 results to filter client-side is fast and cheap
//   - Fuzzy ranking catches typos like "St Marys" vs "St. Mary's"
//
// Cost: ~10-50 reads per search. Acceptable.
//
// Limits:
//   - Won't find "high school" if user typed "Delhi Public" (need prefix match)
//   - For full-text "anywhere" search, V2 should move to Algolia/Typesense
// =====================================================================

/**
 * Compute simple similarity score (0-100) between two normalized strings.
 * Higher = better match. Used to rank fuzzy results.
 */
function fuzzyScore(query, target) {
  if (!query || !target) return 0;
  if (target === query) return 100;
  if (target.startsWith(query)) return 90;
  if (target.includes(query)) return 70;
  // Token overlap: how many query words appear in target?
  const qTokens = query.split(' ').filter(t => t.length >= 2);
  const tTokens = new Set(target.split(' '));
  if (qTokens.length === 0) return 0;
  const matches = qTokens.filter(qt => tTokens.has(qt) || target.includes(qt)).length;
  return Math.round((matches / qTokens.length) * 60);
}

/**
 * Search groups by type, name, and optionally city.
 * Returns up to 10 best matches, ranked by relevance.
 *
 * @param {string} type   - 'alma' | 'company' | 'society' | 'family' | 'business'
 * @param {string} name   - User's search query for the group name
 * @param {string} city   - Optional city filter (substring match)
 * @returns {Promise<Array>} Array of group objects with id, ranked best-first
 */
export async function searchGroupsByCriteria({ type, name, city }) {
  if (!type || !name) return [];
  
  const normalizedQuery = normalizeSearchKey(name);
  if (normalizedQuery.length < 2) return []; // require at least 2 chars
  
  try {
    // Step 1: Firestore prefix query on searchKey.
    // The first word of the query is the "anchor" we prefix-match.
    // Example: query "st marys high" → anchor "st" → matches "st marys...", "st xaviers..."
    const firstToken = normalizedQuery.split(' ')[0];
    const upperBound = firstToken + '\uf8ff'; // Unicode trick: matches all strings starting with firstToken
    
    const q = query(
      collection(db, 'groups'),
      where('type', '==', type),
      where('searchKey', '>=', firstToken),
      where('searchKey', '<=', upperBound),
      limit(50)  // fetch up to 50, rank client-side
    );
    
    const snap = await getDocs(q);
    let candidates = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    
    // Step 2: Filter by city if provided (substring match, case-insensitive)
    if (city && city.trim().length >= 2) {
      const cityNorm = normalizeSearchKey(city);
      candidates = candidates.filter(g => {
        const groupCity = normalizeSearchKey(g.city || '');
        return groupCity.includes(cityNorm) || cityNorm.includes(groupCity);
      });
    }
    
    // Step 3: Score and rank by fuzzy match against full query
    const scored = candidates.map(g => ({
      ...g,
      _score: fuzzyScore(normalizedQuery, g.searchKey || normalizeSearchKey(g.name || '')),
    }));
    
    // Keep only meaningful matches (score >= 30), sort best-first, cap at 10
    return scored
      .filter(g => g._score >= 30)
      .sort((a, b) => b._score - a._score)
      .slice(0, 10)
      .map(({ _score, ...rest }) => rest); // drop internal score before returning
  } catch (err) {
    // Composite index missing? Firestore throws a specific error
    if (err.code === 'failed-precondition') {
      console.error('Firestore index missing for groups search. Deploy firestore.indexes.json.');
    } else {
      console.error('Group search failed:', err);
    }
    return [];
  }
}
// =====================================================================
// REPORTS — Play Store compliance: real reports collection + moderation
// 
// Path: /reports/{reportId}  (top-level — admin can query by groupId)
//
// Fields:
//   groupId         — group where the report was filed
//   reporterId      — UID of the user submitting the report
//   reportedId      — UID of the user being reported
//   reason          — short label: 'abuse' | 'spam' | 'hate' | 'impersonation' | 'inappropriate' | 'other'
//   detail          — optional free-text from reporter
//   status          — 'open' | 'dismissed' | 'warned' | 'removed'
//   createdAt       — server timestamp
//   reviewerId      — UID of admin who resolved (null until resolved)
//   reviewedAt      — server timestamp (null until resolved)
//   reviewerAction  — same as final status ('dismissed' | 'warned' | 'removed')
//
// Rate limit: one report per (reporter, reported) per 24h, enforced client-side.
// Firestore rules: anyone signed in can create; only group admins (or AanganAdmin)
// can read/update.
// =====================================================================

/**
 * Submit a new report. Performs a 24h rate-limit pre-check on the client.
 * Returns the new report ID on success, or { duplicate: true } if user
 * already reported this person in the last 24 hours.
 */
export async function submitReport({ groupId, reporterId, reportedId, reason, detail }) {
  if (!groupId || !reporterId || !reportedId || !reason) {
    throw new Error('groupId, reporterId, reportedId, reason are all required');
  }
  if (reporterId === reportedId) {
    throw new Error('Cannot report yourself');
  }
  
  // Rate-limit: check if this reporter has reported this person in the last 24h.
  try {
    const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const q = query(
      collection(db, 'reports'),
      where('reporterId', '==', reporterId),
      where('reportedId', '==', reportedId),
      where('createdAt', '>', cutoff),
      limit(1)
    );
    const dupSnap = await getDocs(q);
    if (!dupSnap.empty) {
      return { duplicate: true };
    }
  } catch (e) {
    // If the dup check fails (e.g. missing index), proceed anyway — don't block reporting.
    console.warn('Report dup-check failed (non-fatal):', e);
  }
  
  const reportData = {
    groupId,
    reporterId,
    reportedId,
    reason,
    detail: (detail || '').trim().slice(0, 500),
    status: 'open',
    createdAt: serverTimestamp(),
    reviewerId: null,
    reviewedAt: null,
    reviewerAction: null,
  };
  
  const ref = await addDoc(collection(db, 'reports'), reportData);
  return { id: ref.id, duplicate: false };
}

/**
 * Subscribe to OPEN reports for a specific group. Admin-only — Firestore
 * rules enforce read access. Returns unsubscribe fn.
 */
export function subscribeToGroupReports(groupId, callback) {
  if (!groupId) return () => {};
  const q = query(
    collection(db, 'reports'),
    where('groupId', '==', groupId),
    where('status', '==', 'open'),
    orderBy('createdAt', 'desc'),
    limit(100)
  );
  return onSnapshot(q,
    (snap) => {
      const reports = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      callback(reports);
    },
    (err) => {
      if (err.code !== 'permission-denied') {
        console.error('Reports subscription error:', err);
      }
    }
  );
}

/**
 * Resolve a report — dismiss, warn, or remove. Admin-only.
 * For 'warn': writes a notification to the reported user's inbox.
 * For 'remove': caller is expected to call removeGroupMember separately
 * (this function just updates the report doc; the actual removal stays
 * with the existing removeGroupMember pipeline so we don't duplicate logic).
 */
export async function resolveReport({ reportId, reviewerId, action, groupId, reportedId }) {
  if (!reportId || !reviewerId || !action) {
    throw new Error('reportId, reviewerId, action required');
  }
  if (!['dismiss', 'warn', 'remove'].includes(action)) {
    throw new Error('action must be dismiss, warn, or remove');
  }
  
  const statusMap = { dismiss: 'dismissed', warn: 'warned', remove: 'removed' };
  
  // Update the report doc
  await setDoc(doc(db, 'reports', reportId), {
    status: statusMap[action],
    reviewerId,
    reviewedAt: serverTimestamp(),
    reviewerAction: statusMap[action],
  }, { merge: true });
  
  // Side-effects per action
  if (action === 'warn' && reportedId) {
    // Two writes for a warning, both best-effort:
    //   1. /users/{reportedId}.pendingWarning  — primary signal. The app reads
    //      this on load AND subscribes for live updates. If present, a blocking
    //      modal forces the user to acknowledge the warning before continuing.
    //   2. /notifications/{reportedId}/items/  — audit / future inbox surface
    //      (Notifications screen will subscribe to this in V1.4). Not used yet
    //      by any active screen but keeps the per-warning trail readable.
    try {
      await setDoc(doc(db, 'users', reportedId), {
        pendingWarning: {
          text: 'An admin reviewed a report about your activity. Please follow the Community Guidelines — continued violations may result in removal from the batch.',
          groupId: groupId || null,
          reviewerId,
          issuedAt: serverTimestamp(),
        },
      }, { merge: true });
    } catch (e) {
      console.warn('Could not write pendingWarning on user doc (non-fatal):', e);
    }
    try {
      await addDoc(collection(db, 'notifications', reportedId, 'items'), {
        kind: 'warning',
        groupId: groupId || null,
        text: 'An admin reviewed a report about your activity.',
        subtext: 'Please follow the Community Guidelines. Continued violations may result in removal.',
        createdAt: serverTimestamp(),
        readAt: null,
      });
    } catch (e) {
      console.warn('Could not write warning notification (non-fatal):', e);
    }
  }
  // 'remove' action: caller does removeGroupMember separately. We don't duplicate it here.
}

/**
 * Subscribe to a user's pendingWarning field in real-time. Used to show
 * a blocking acknowledgment modal the moment an admin issues a warning.
 * Returns unsubscribe fn.
 */
export function subscribeToPendingWarning(userId, callback) {
  if (!userId) return () => {};
  return onSnapshot(doc(db, 'users', userId),
    (snap) => {
      const data = snap.exists() ? snap.data() : {};
      callback(data.pendingWarning || null);
    },
    (err) => {
      if (err.code !== 'permission-denied') {
        console.error('pendingWarning subscription error:', err);
      }
    }
  );
}

/**
 * Acknowledge and clear a pendingWarning. Called when the user taps
 * "I understand" on the warning modal.
 */
export async function acknowledgePendingWarning(userId) {
  if (!userId) return;
  // Use deleteField via setDoc with FieldValue.delete()? merge:true would
  // preserve the field if we set undefined. Cleaner: explicit null clear.
  // We set to null instead of deleting so the doc still reflects "no
  // pending warning" and the subscription fires the null callback.
  await setDoc(doc(db, 'users', userId), {
    pendingWarning: null,
  }, { merge: true });
}
// =====================================================================
// SUPPORT CHAT HELPERS (Session 5a)
// =====================================================================
//
// Support chats reuse the existing /chats/{chatId} schema with a
// discriminator field chatType: 'support'. The Firestore rules
// (updated in Session 5a) grant admin (isPlatformAdmin()) read/write
// access ONLY to chats where chatType == 'support'; peer chats between
// two normal users remain private.
//
// Deterministic chatId via makeChatId(businessOwnerUid, AANGAN_SUPPORT_UID)
// means a business owner can never accidentally end up with two support
// chats — repeated taps of "Chat with Aangan support" open the same chat.

// aangansupport@gmail.com — the shared "support side" participant for
// every support chat. When a real admin (Rajasekhar or aangansupport
// itself) replies via the super-admin panel, the individual admin UID
// goes on the message doc's fromId (for internal audit), but the CHAT
// participant is always aangansupport so the business owner sees a
// stable "you're talking to Aangan Support" experience.
export const AANGAN_SUPPORT_UID = 'RS0iaHZppgQeaNT4bGt7KWRbWyZ2';

// Display name shown on the business side for any admin-authored message.
// Rajasekhar (YYCz...) and aangansupport (RS0i...) both render as this
// on the business chat UI — the individual admin identity is intentionally
// abstracted away for the customer-facing surface.
export const AANGAN_SUPPORT_DISPLAY_NAME = 'Aangan Support';

// All admin UIDs that should display as "Aangan Support" on the business
// side. Kept as an array so future additions (another support agent) are
// a one-line change here. The super-admin auth gate at /?admin=1 already
// enforces superAdmin === 1 in Firestore, so this array only needs
// entries for admins who actively reply on support chats.
const AANGAN_SUPPORT_SENDER_UIDS = [
  AANGAN_SUPPORT_UID,                          // aangansupport@gmail.com
  'YYCzVhMQeRTfRatuovwelQ4oM8d2',              // rajasekhar91.d@gmail.com
];

// Returns true if a message's fromId should display as "Aangan Support"
// on the business chat UI. Used by SupportChatModal to render the sender
// name label without hardcoding UIDs in JSX.
export function isAanganSupportSender(uid) {
  return !!uid && AANGAN_SUPPORT_SENDER_UIDS.includes(uid);
}

// Open (or create if not existing) the support chat for a given business
// owner. Idempotent — repeated calls return the same chatId thanks to
// makeChatId's deterministic ordering.
//
// Creation payload sets chatType: 'support' which the extended Firestore
// rules require for admin read/write access. Participants are the owner
// UID and AANGAN_SUPPORT_UID sorted (matches makeChatId + rule expects
// participants.size() == 2 on create). requesterId is the owner (must
// match request.auth.uid per the create rule).
//
// Args:
//   ownerUid (string, required) — the business owner initiating the chat
//
// Returns: { chatId, wasCreated }
//   - chatId: string, always
//   - wasCreated: true if we just created the chat, false if it existed
//
// Firestore cost:
//   - 1 read (getDoc to check existence)
//   - 1 write IF chat is new (setDoc)
//
// Throws: Firestore errors, or Error if ownerUid missing.
export async function openOrCreateSupportChat({ ownerUid, ownerName }) {
  if (!ownerUid) throw new Error('openOrCreateSupportChat: ownerUid required');
  if (ownerUid === AANGAN_SUPPORT_UID) {
    throw new Error('openOrCreateSupportChat: aangansupport cannot open a support chat with itself');
  }

  // Denormalized display name for the admin inbox. Persisted on the chat
  // doc so listSupportChatsForAdmin can render human-readable rows
  // (owner name + preview) with ZERO extra reads per row — the alternative
  // is joining /users per chat which doubles the page's read count. See
  // the Session 2 cost-discipline note about denormalized ownerName as
  // the "right fix later" for owner-column rendering; this is that fix
  // for the support-inbox surface. Falls back to '' when caller doesn't
  // pass a name (SupportInboxPage displays the raw UID in that case).
  const nameToWrite = (ownerName || '').trim();

  const chatId = makeChatId(ownerUid, AANGAN_SUPPORT_UID);
  const chatRef = doc(db, 'chats', chatId);
  const snap = await getDoc(chatRef);

  if (snap.exists()) {
    // A chat between this owner and aangansupport already exists. Two
    // possibilities: (a) previous support session (already has
    // chatType:'support'), or (b) they were peer-chatting BEFORE support
    // chat existed (e.g. Rajasekhar acting as a merchant testing
    // messaging with aangansupport). In case (b), the chat has no
    // chatType field and the extended rules won't grant admin access
    // to it — the chat is invisible to the super-admin panel.
    //
    // Guarantee chatType:'support' on all "opened" support chats via a
    // one-field merge write. Idempotent — no-op when already set.
    // Deliberately does NOT touch participants (rule requires unchanged)
    // or any other field.
    //
    // ALSO refresh ownerName on each open — cheapest way to keep the
    // denormalized name from going stale when the owner renames
    // themselves on /users. Owners open their own support chats, so
    // their current name naturally propagates to the chat doc.
    const data = snap.data() || {};
    const patch = {};
    if (data.chatType !== 'support') patch.chatType = 'support';
    if (nameToWrite && data.ownerName !== nameToWrite) patch.ownerName = nameToWrite;
    if (Object.keys(patch).length > 0) {
      await setDoc(chatRef, patch, { merge: true });
    }
    return { chatId, wasCreated: false };
  }

  // First-time creation. The Firestore create rule requires:
  //   - requester (== request.auth.uid) is one of participants
  //   - participants.size() == 2
  //   - requesterId == request.auth.uid
  // The owner IS the current auth user here (this helper only runs client-
  // side, called from MerchantApp). chatType: 'support' is what unlocks
  // admin read/write access via the extended rules.
  const participants = [ownerUid, AANGAN_SUPPORT_UID].sort();
  await setDoc(chatRef, {
    participants,
    requesterId: ownerUid,
    chatType: 'support',
    ownerName: nameToWrite,
    createdAt: serverTimestamp(),
    lastMessage: '',
    lastMessageAt: serverTimestamp(),
    lastReadAt: { [ownerUid]: serverTimestamp() },
  });

  return { chatId, wasCreated: true };
}

/**
 * Session C1 — submit a report against a BUSINESS wall post.
 *
 * V1 submitReport (above) is member-reports-member, scoped to a group, and
 * routes to that batch's admins. A business wall post has no group and no
 * group admin, so it cannot use that path. Business reports instead go to the
 * PLATFORM admin, written into the SAME /reports collection but discriminated
 * by targetType so the super-admin Reports page can render them distinctly
 * from V1 member reports (which have no targetType).
 *
 * Shape (business report):
 *   targetType   'business_post'   ← discriminator; V1 member reports omit this
 *   targetId     the post id being reported
 *   businessId   the business the post belongs to (for admin context + suspend)
 *   reporterId   who reported (auth uid)
 *   reporterName / reporterPhone  denormalised so the admin can follow up
 *                without a users/{uid} join (may be empty if unknown)
 *   reason       one of the wall-post reason keys
 *   detail       optional free text, capped 500
 *   status       always begins 'open' (same lifecycle as V1: dismissed/warned/removed)
 *
 * Dedupe: one report per (reporter, target post) per 24h, mirroring V1.
 * Non-fatal if the dedupe query fails (missing index) — reporting still works.
 */
export async function submitBusinessReport({
  businessId,
  targetId,
  reporterId,
  reporterName,
  reporterPhone,
  reason,
  detail,
}) {
  if (!businessId || !targetId || !reporterId || !reason) {
    throw new Error('businessId, targetId, reporterId, reason are all required');
  }

  // Rate-limit: has this reporter already reported this post in the last 24h?
  try {
    const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const q = query(
      collection(db, 'reports'),
      where('reporterId', '==', reporterId),
      where('targetId', '==', targetId),
      where('createdAt', '>', cutoff),
      limit(1)
    );
    const dupSnap = await getDocs(q);
    if (!dupSnap.empty) {
      return { duplicate: true };
    }
  } catch (e) {
    console.warn('Business report dup-check failed (non-fatal):', e);
  }

  const reportData = {
    targetType: 'business_post',
    targetId,
    businessId,
    reporterId,
    reporterName: (reporterName || '').trim().slice(0, 120),
    reporterPhone: (reporterPhone || '').trim().slice(0, 20),
    reason,
    detail: (detail || '').trim().slice(0, 500),
    status: 'open',
    createdAt: serverTimestamp(),
    reviewerId: null,
    reviewedAt: null,
    reviewerAction: null,
  };

  const ref = await addDoc(collection(db, 'reports'), reportData);
  return { id: ref.id, duplicate: false };
}

/**
 * Session C2a — submit a report against an ORDER CHAT conversation.
 *
 * Third report shape in the shared /reports collection:
 *   V1 member report   — groupId + reportedId, no targetType. Routed to that
 *                        batch's admins.
 *   C1 business post   — targetType 'business_post', businessId + targetId.
 *   C2a order chat     — targetType 'order_chat' (this one).
 *
 * An order chat is a 1:1 conversation between a customer and a business
 * owner about one order. It has no group and no group admin, so like a
 * business post it escalates to the PLATFORM admin.
 *
 * Shape (order-chat report):
 *   targetType      'order_chat'    ← discriminator
 *   targetId        the order id (chatId === order docId in V2)
 *   orderId         same value, named explicitly so the admin drawer can
 *                   resolve /orders/{orderId} without knowing the aliasing
 *   businessId      the business the order belongs to
 *   reportedUserId  the counterparty's uid — see NAMING note below
 *   reporterRole    'customer' | 'merchant' — who filed it. Both sides of an
 *                   order chat can report, so the admin needs to know which.
 *   reporterId      who reported (auth uid)
 *   reporterName    denormalised for admin follow-up (may be empty)
 *   reason          one of the order-chat reason keys
 *   detail          optional free text, capped 500
 *   status          always begins 'open'
 *
 * NAMING — reportedUserId, NOT reportedId. This is deliberate and load-bearing.
 * V1's submitReport dedupes with where('reportedId','==',uid). A Firestore
 * equality filter only matches documents that HAVE the field, so keeping the
 * counterparty under a different key means order-chat reports are invisible to
 * that query. If we reused 'reportedId', reporting a merchant here would
 * silently swallow a legitimate, unrelated V1 batch report against the same
 * person for the next 24h — different context, different admin, never seen.
 * C1's business_post shape sets the precedent: V2 reports don't use reportedId.
 *
 * Dedupe: one report per (reporter, order) per 24h, on targetId — matching C1.
 * Non-fatal if the dedupe query fails (missing index) — reporting still works.
 */
export async function submitOrderChatReport({
  orderId,
  businessId,
  reportedUserId,
  reporterId,
  reporterName,
  reporterRole,
  reason,
  detail,
}) {
  if (!orderId || !businessId || !reportedUserId || !reporterId || !reason) {
    throw new Error('orderId, businessId, reportedUserId, reporterId, reason are all required');
  }
  if (reportedUserId === reporterId) {
    throw new Error('Cannot report yourself');
  }

  // Rate-limit: has this reporter already reported this order in the last 24h?
  try {
    const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const q = query(
      collection(db, 'reports'),
      where('reporterId', '==', reporterId),
      where('targetId', '==', orderId),
      where('createdAt', '>', cutoff),
      limit(1)
    );
    const dupSnap = await getDocs(q);
    if (!dupSnap.empty) {
      return { duplicate: true };
    }
  } catch (e) {
    console.warn('Order-chat report dup-check failed (non-fatal):', e);
  }

  const reportData = {
    targetType: 'order_chat',
    targetId: orderId,
    orderId,
    businessId,
    reportedUserId,
    reporterId,
    reporterName: (reporterName || '').trim().slice(0, 120),
    reporterRole: reporterRole === 'merchant' ? 'merchant' : 'customer',
    reason,
    detail: (detail || '').trim().slice(0, 500),
    status: 'open',
    createdAt: serverTimestamp(),
    reviewerId: null,
    reviewedAt: null,
    reviewerAction: null,
  };

  const ref = await addDoc(collection(db, 'reports'), reportData);
  return { id: ref.id, duplicate: false };
}

// =====================================================================
// BLOCK — Session C2b
// Path: /users/{blockerUid}/blockedUsers/{blockedUid}
//         { blockedAt, name, photoUrl }
//
// WHAT BLOCK DOES, AND WHAT IT DOESN'T
// ------------------------------------
// Blocking is user-to-user and SYMMETRIC in effect: once either side
// blocks, neither can message the other, and neither can place a new
// order with the other. It is enforced in firestore.rules, not here —
// these helpers only write the block doc. The rules do the rest, at the
// single chokepoint every message goes through (/chats/{id}/messages).
//
// It does NOT hide the blocked person's content. Their wall posts, their
// business listing, their profile all remain visible. That is a decision,
// not an omission: hiding content means client-side filtering at every
// read surface forever, and it can never be complete because Firestore
// cannot filter "not in this other collection" server-side. A block that
// promises "you'll never see them again" and then leaks their shop into
// the directory is worse than one that says plainly what it does. The
// in-app copy states the limit.
//
// WHY THE PERSON, NOT THE BUSINESS
// --------------------------------
// In an order chat a customer's counterparty is the business OWNER — a
// person. Blocking a businessId would leave that same owner free to reach
// the same customer from their personal V1 account: different surface,
// different identifier, same human. Blocking the uid closes every door at
// once. The cost is that it also blocks that owner's other businesses,
// which the UI says out loud rather than doing quietly.
//
// PRIVACY
// -------
// The subcollection is readable ONLY by the blocker (see firestore.rules).
// A blocked user cannot discover they were blocked — they get a plain
// permission-denied on send, indistinguishable from a network failure, and
// the UI must keep it that way. Confirming a block is what turns an in-app
// problem into an offline one.
// =====================================================================

/**
 * Block a user.
 *
 * Idempotent: setDoc on a deterministic path, so double-tapping Block is
 * harmless rather than creating a second doc.
 *
 * name/photoUrl are denormalised so the Settings list renders from one
 * query instead of N joins. They are a snapshot — if the blocked user
 * later renames themselves the list keeps the old name. That is fine and
 * arguably better: you blocked the person you remember, and re-reading
 * every blocked user's profile to keep a list you rarely open current
 * would be reads spent on nothing.
 */
export async function blockUser({ blockerId, blockedId, name, photoUrl }) {
  if (!blockerId || !blockedId) throw new Error('blockerId and blockedId required');
  if (blockerId === blockedId) throw new Error('Cannot block yourself');

  const ref = doc(db, 'users', blockerId, 'blockedUsers', blockedId);
  await setDoc(ref, {
    blockedAt: serverTimestamp(),
    name: (name || '').trim().slice(0, 120),
    photoUrl: photoUrl || null,
  });
  return { blockedId };
}

/**
 * Unblock a user. Hard delete, deliberately.
 *
 * Most collections in this app soft-delete to preserve an audit trail. A
 * block is not moderation evidence — it is the user's own private choice
 * about their own experience. Keeping a tombstone of who someone once
 * blocked would be its own small betrayal, so the doc goes.
 */
export async function unblockUser({ blockerId, blockedId }) {
  if (!blockerId || !blockedId) throw new Error('blockerId and blockedId required');
  await deleteDoc(doc(db, 'users', blockerId, 'blockedUsers', blockedId));
  return { blockedId };
}

/**
 * Subscribe to the users I have blocked. Returns the unsubscribe function.
 *
 * A live subscription rather than a one-shot read because this list drives
 * UI state in several places at once (the Settings screen, the Block/Unblock
 * label in a chat's overflow menu, the composer's disabled state). A stale
 * "Block" label on someone you just unblocked is the kind of small lie that
 * makes a safety feature feel untrustworthy.
 *
 * Cost is negligible: blocked lists are tiny — single digits for almost
 * everyone — and unbounded only in theory. No pagination for that reason;
 * if someone ever blocks hundreds of people, that is a signal worth looking
 * at rather than a page size worth tuning.
 */
export function subscribeToBlockedUsers(userId, callback) {
  if (!userId) return () => {};
  const q = query(
    collection(db, 'users', userId, 'blockedUsers'),
    orderBy('blockedAt', 'desc')
  );
  return onSnapshot(q,
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      if (err.code !== 'permission-denied') {
        console.error('Blocked users subscription error:', err);
      }
      callback([]);
    }
  );
}

/**
 * One-shot check: have I blocked this person?
 *
 * For call sites that need the answer once and don't want a subscription.
 * Note this can only ever answer about MY OWN blocks — the rules make the
 * reverse direction unreadable by design, so there is deliberately no
 * amIBlockedBy() helper. If they blocked you, the only thing your client
 * ever learns is that a send failed.
 */
export async function isUserBlockedByMe({ blockerId, blockedId }) {
  if (!blockerId || !blockedId) return false;
  try {
    const snap = await getDoc(doc(db, 'users', blockerId, 'blockedUsers', blockedId));
    return snap.exists();
  } catch (e) {
    console.warn('Block check failed:', e);
    return false;
  }
}
